import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, CpuChipIcon, CubeTransparentIcon } from '@heroicons/react/24/outline';

const InvSlide10WhyNow: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Why Now?
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center max-w-3xl"
      >
        AI has changed everything — what would have taken years and millions can now be done in months
      </motion.p>

      <div className="flex gap-8 w-full max-w-5xl">
        {/* The Challenge */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <div
            className="p-6 rounded-xl h-full"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}
          >
            <h3 className="text-xl font-semibold text-[#ef4444] mb-4">The Traditional Challenge</h3>

            <div className="space-y-4 text-[#94a3b8]">
              <p>
                For NSW K-12 Mathematics alone, there are <span className="text-white font-bold">5,200+ learning strategies</span> across 516 modules.
              </p>
              <p>
                Each strategy is an interactive widget: equations, geometry, graphs, coordinate planes, statistics...
              </p>
              <p>
                Traditionally, this would require:
              </p>
              <ul className="list-disc list-inside space-y-1 text-[#ef4444]">
                <li>50+ developers</li>
                <li>Years of development</li>
                <li>Millions in budget</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* The Solution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="flex-1"
        >
          <div
            className="p-6 rounded-xl h-full"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '2px solid rgba(16, 185, 129, 0.4)',
            }}
          >
            <h3 className="text-xl font-semibold text-[#10b981] mb-4 flex items-center gap-2">
              <SparklesIcon className="w-6 h-6" />
              The AI Breakthrough
            </h3>

            <div className="space-y-4 text-[#94a3b8]">
              <p>
                We cracked the code: <span className="text-white font-bold">AI generates learning modules</span> that perfectly interface with our system.
              </p>
              <div className="flex items-start gap-3">
                <CubeTransparentIcon className="w-5 h-5 text-[#10b981] mt-1" />
                <p>Two specs define exact style, look, feel, and language</p>
              </div>
              <div className="flex items-start gap-3">
                <CpuChipIcon className="w-5 h-5 text-[#10b981] mt-1" />
                <p>Modules expose classes for random question generation</p>
              </div>
              <div className="flex items-start gap-3">
                <SparklesIcon className="w-5 h-5 text-[#10b981] mt-1" />
                <p>Perfect interfaces with ins and outs of our system</p>
              </div>

              <div
                className="mt-4 p-3 rounded-lg text-center"
                style={{ background: 'rgba(16, 185, 129, 0.2)' }}
              >
                <p className="text-white font-semibold">
                  "This was the hardest part — and we cracked it."
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="mt-8 px-8 py-5 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
          border: '2px solid rgba(14, 165, 233, 0.4)',
        }}
      >
        <p className="text-xl text-white text-center">
          Result: We can generate modules on the fly, generate questions on the fly, and scale globally
        </p>
      </motion.div>
    </div>
  );
};

export default InvSlide10WhyNow;
